import type { AccountingSplit } from '@/domain/rendition/Rendition'
import type { Split, CostCenter, Account } from '@/types/split'

// Bridges the rendition's id-based AccountingSplit and the platform's object-based Split (used by Splits.vue),
// so the shared accounting editor can be reused while the draft keeps storing AccountingSplit.

/**
 * AccountingSplit → platform Split: resolves the cost-center/account objects from the catalogs by id.
 * Falls back to the id + label stored on the split when the catalog lacks it (inactive or policy-filtered
 * option) — otherwise the editor would render blank a dimension the split actually carries.
 */
export function accSplitToSplit(s: AccountingSplit, costCenters: CostCenter[], accounts: Account[]): Split {
  return {
    cost_center:
      s.costCenterId != null
        ? costCenters.find((c) => c.id === s.costCenterId) ?? { id: s.costCenterId, name: s.costCenterLabel, code: '' }
        : undefined,
    account:
      s.accountId != null
        ? accounts.find((a) => a.id === s.accountId) ?? { id: s.accountId, name: s.accountLabel, code: s.accountCode ?? '' }
        : undefined,
    percentage: s.percentage,
    amount: undefined,
    custom_fields_answers: { ...(s.customFieldsAnswers ?? {}) },
  }
}

/**
 * Backend split `custom_field_answers` (array of `{ custom_field_id, value }`) → the id→value record an
 * AccountingSplit stores. Mirrors the platform's `useDocumentLineNormalizer` so a rendition's split custom
 * fields survive a round trip instead of being dropped on hydrate and wiped on the next save.
 */
export function customFieldAnswersFromApi(
  raw: { custom_field_id: number; value: string }[] | undefined,
): Record<number, string> {
  const out: Record<number, string> = {}
  for (const a of raw ?? []) {
    // Skip a blank value (empty string), like the platform's normalizer — an empty answer is "unset".
    if (a?.custom_field_id != null && a.value) out[a.custom_field_id] = a.value
  }
  return out
}

/** Platform Split → AccountingSplit: flattens the objects back to ids + labels for storage/payload. */
export function splitToAccSplit(s: Split): AccountingSplit {
  return {
    costCenterId: s.cost_center?.id,
    costCenterLabel: s.cost_center?.name ?? '',
    accountId: s.account?.id,
    accountLabel: s.account?.name ?? '',
    accountCode: s.account?.code,
    customFieldsAnswers: s.custom_fields_answers ? { ...s.custom_fields_answers } : undefined,
    percentage: s.percentage ?? 0,
  }
}
